import React from 'react'
import { View, StyleSheet, TouchableOpacity, ScrollView, Image } from 'react-native'
import { useNavigationState, useTheme } from '@react-navigation/native'
import { Text, Icon } from '@components'
import { getMenuItemsWeb } from '../../screens/menu/constants/menuItems'
import { Theme } from '@theme'
import { profileAvatar } from '@assets'
import { WebMenuItem } from './WebMenuItem'
import { WebSidebarToggle } from './WebSidebarToggle'
import { useScreen } from '../../hooks/useScreen'
import { useNavigation } from '@react-navigation/native'
import { getActiveRouteNameChain } from '../navigationUtils'
import { useAuth, useLanguage } from '@context'
import { useGetProfile, useUnreadNotificationsCount, useUnreadConversationsCount } from '@hooks'
import { Config } from '@config'

const appVersion = Config.APP_VERSION ?? ''

interface WebSidebarProps {
  isCollapsed: boolean
  onToggle: () => void
}

const Separator = () => {
  const theme = useTheme()
  const styles = getStyle(theme)
  return <View style={styles.separator} />
}

export const WebSidebar: React.FC<WebSidebarProps> = ({ isCollapsed, onToggle }) => {
  const theme = useTheme()
  const { isMobile, isDesktop } = useScreen()
  const styles = getStyle(theme, !isDesktop)
  const { data: profile } = useGetProfile()
  const navigation = useNavigation()
  const { signOut: authSignOut } = useAuth()
  const { t } = useLanguage()
  const { unreadCount } = useUnreadNotificationsCount()
  const { unreadCount: unreadMessagesCount } = useUnreadConversationsCount()

  const routeNameChain = useNavigationState((state) => getActiveRouteNameChain(state))
  const isProfileActive = routeNameChain.includes('ProfileHome')

  const menuItems = getMenuItemsWeb(t)

  const bottomItems = [
    {
      id: '10',
      title: t('menuItems.qrCode'),
      icon: 'qrCode',
      screen: 'QRCode',
    },
    {
      id: '11',
      title: t('menuItems.settings'),
      icon: 'settings',
      screen: 'Settings',
      showDivider: true,
    },
  ]

  const topItems = [
    {
      id: '12',
      title: t('webNav.allContacts'),
      icon: 'people',
      screen: 'ListContacts',
    },
    {
      id: '13',
      title: t('webNav.messaging'),
      icon: 'bubbleRound',
      screen: 'ListMessages',
    },
    {
      id: '14',
      title: t('webNav.notifications'),
      icon: 'bell',
      screen: 'Notifications',
      showDivider: true,
    },
    {
      id: '15',
      title: t('tabNavigation.chatbot'),
      icon: 'star',
      screen: 'Chatbot',
      showDivider: true,
    },
  ]

  const closeIfMobile = () => {
    if (!isCollapsed && isMobile) {
      onToggle()
    }
  }

  const handleItemPress = (screen: string, navigator: 'Tab' | 'Menu') => {
    // Always navigate to the correct parent navigator first, then to the screen
    ;(navigation as any).navigate(navigator, {
      screen,
    })
    closeIfMobile()
  }

  const handleProfilePress = () => {
    ;(navigation as any).navigate('Profile', {
      screen: 'ProfileHome',
    })
    closeIfMobile()
  }

  const signOut = () => {
    authSignOut()
    closeIfMobile()
  }

  return isCollapsed && isMobile ? null : (
    <View
      style={StyleSheet.flatten([
        styles.sidebar,
        isMobile && styles.sidebarAbsolute,
        isCollapsed && styles.sidebarCollapsed,
      ])}
    >
      {!isMobile && <WebSidebarToggle isCollapsed={isCollapsed} onToggle={onToggle} />}
      {/* Main Navigation */}
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent}>
        <View style={styles.top}>
          {/* Menu Items */}
          {topItems.map((item) => (
            <WebMenuItem
              key={item.id}
              id={item.id}
              title={item.title}
              icon={item.icon}
              screen={item.screen}
              isCollapsed={isCollapsed}
              badgeCount={
                item.screen === 'Notifications'
                  ? unreadCount
                  : item.screen === 'ListMessages'
                    ? unreadMessagesCount
                    : 0
              }
              onPress={(screen) => handleItemPress(screen, 'Tab')}
            />
          ))}
          <Separator />
          {menuItems.map((item) => (
            <WebMenuItem
              key={item.id}
              id={item.id}
              title={item.title}
              icon={item.icon}
              screen={item.screen}
              isCollapsed={isCollapsed}
              onPress={(screen) => handleItemPress(screen, 'Menu')}
            />
          ))}
        </View>
        <View style={styles.bottom}>
          <Separator />
          {bottomItems.map((item) => (
            <WebMenuItem
              key={item.id}
              id={item.id}
              title={item.title}
              icon={item.icon}
              screen={item.screen}
              isCollapsed={isCollapsed}
              onPress={(screen) => handleItemPress(screen, 'Menu')}
            />
          ))}
          <Separator />
          <TouchableOpacity
            style={styles.profileButton}
            onPress={handleProfilePress}
            activeOpacity={0.7}
          >
            <View style={styles.itemContent}>
              <Image
                source={profile?.avatarUrl ? { uri: profile.avatarUrl } : profileAvatar}
                style={styles.avatar}
              />
              {!isCollapsed && (
                <Text
                  type="bodySmall"
                  style={StyleSheet.flatten([
                    styles.profileText,
                    isProfileActive && { color: theme.colors.primary },
                  ] as any)}
                >
                  {t('webNav.myProfile')}
                </Text>
              )}
            </View>
          </TouchableOpacity>
        </View>
        {/* Logout */}
        <View>
          <TouchableOpacity style={styles.logoutButton} onPress={signOut} activeOpacity={0.7}>
            <View style={styles.itemContent}>
              <Icon name="logout" size={18} color={theme.colors.error} />
              {!isCollapsed && (
                <Text type="bodySmall" style={styles.logoutText}>
                  {t('webNav.signOut')}
                </Text>
              )}
            </View>
          </TouchableOpacity>
          {!isCollapsed && appVersion ? (
            <Text type="bodyExtraSmall" style={styles.versionText}>
              {t('menu.version', { version: appVersion })}
            </Text>
          ) : null}
        </View>
      </ScrollView>
    </View>
  )
}

const getStyle = (theme: Theme, isSmall?: boolean) =>
  StyleSheet.create({
    sidebar: {
      width: isSmall ? 220 : 280,
      backgroundColor: 'rgb(243,250,255)',
      display: 'flex',
      flexDirection: 'column',
    },
    sidebarAbsolute: {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      zIndex: 1,
      shadowColor: '#000',
      shadowOpacity: 0.15,
      shadowRadius: 5,
      shadowOffset: { width: 2, height: 0 },
      elevation: 3,
    },
    sidebarCollapsed: {
      width: 60,
    },
    title: {
      marginLeft: 12,
      fontWeight: '600',
    },
    scrollView: {
      flex: 1,
    },
    scrollViewContent: {
      flexGrow: 1,
    },
    top: {
      flex: 1,
    },
    sectionTitle: {
      paddingHorizontal: 16,
      paddingVertical: 8,
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    navItem: {
      marginHorizontal: 8,
      borderRadius: 8,
      cursor: 'pointer',
    },
    navItemContent: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 12,
      paddingVertical: 12,
    },
    navItemText: {
      marginLeft: 12,
      fontWeight: '500',
    },
    bottom: {
      justifyContent: 'flex-end',
    },
    logoutText: {
      marginLeft: 12,
      fontSize: 13,
      color: theme.colors.error,
    },
    profileButton: {
      marginHorizontal: theme.spacing.md,
      marginVertical: theme.spacing.xs,
      borderRadius: 6,
      cursor: 'pointer',
    },
    logoutButton: {
      marginHorizontal: theme.spacing.xl,
      marginBottom: theme.spacing.md,
      marginVertical: theme.spacing.xs,
      borderRadius: 6,
      cursor: 'pointer',
    },
    itemContent: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    profileText: {
      marginLeft: 12,
      fontSize: 13,
      color: theme.colors.text,
    },
    avatar: {
      width: 35,
      height: 35,
      borderRadius: 18,
    },
    separator: {
      height: 1,
      backgroundColor: theme.colors.grey30,
      marginVertical: theme.spacing.xs,
      marginHorizontal: 20,
      borderRadius: 1,
    },
    versionText: {
      textAlign: 'right',
      color: '#999',
      paddingBottom: theme.spacing.md,
      paddingHorizontal: theme.spacing.xl,
    },
  })
